/**
 * The login gate — rendered INSTEAD of the LV shell whenever there is no
 * valid session. The public masthead (LvHeader) tops the page — the LV
 * stays sign-in-gated, so every masthead destination renders LOCKED and
 * routes into the same sign-in flow below — over a single centered card in
 * the deployment's own theme (the learnerView config's primary colour and
 * font, same as the shell): what this is, and one Sign in button that starts
 * the SSO round-trip (POST /LearnerView/api/auth/start → browser leaves for
 * the LMS, which returns to /LearnerView/api/auth/cb and lands back here
 * with a session).
 *
 * When the server answers 503 the deployment has no auth configured
 * (LV_DATABASE_URL / LMS_BASE_URL unset, Postgres down, or the LMS-side
 * learnerView.sso* flags missing) — the button is replaced by a polite
 * setup state, because clicking can never succeed. Any other failure is
 * treated as transient and gets a retry.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, CircularProgress, CssBaseline, Paper, Stack, ThemeProvider, Typography,
} from '@mui/material'
import LoginIcon from '@mui/icons-material/Login'
import {
  brandLogoAlt, brandLogoUrl, fetchLvConfig, homeTabLabel, productName, resolveSurfaces, type LvClientConfig,
} from './lvConfig'
import { buildLvTheme, useBrandAssets } from './brandTheme'
import { LV_SURFACES } from './registry'
import LvHeader from './LvHeader'
import { useBrandMode } from './useBrandMode'

type GateState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'starting' }
  | { kind: 'unconfigured'; message: string }
  | { kind: 'error'; message: string }

export default function LoginGate() {
  const [cfg, setCfg] = useState<LvClientConfig | null>(null)
  const [state, setState] = useState<GateState>({ kind: 'loading' })

  useEffect(() => {
    let live = true
    void fetchLvConfig().then((c) => {
      if (live) {
        setCfg(c)
        setState((s) => (s.kind === 'loading' ? { kind: 'ready' } : s))
      }
    })
    return () => { live = false }
  }, [])

  // The shared builder: the gate wears the deployment's brand (colour, fonts,
  // corner style, logo, favicon) exactly as the shell behind it does — and
  // the same light/dark policy + choice the masthead's Appearance menu sets.
  const { mode, allowed: appearanceAllowed, choose: chooseAppearanceMode } = useBrandMode(cfg)
  const theme = useMemo(() => buildLvTheme(cfg, mode), [cfg, mode])
  useBrandAssets(cfg)
  const logoUrl = brandLogoUrl(cfg)

  // A deployment may name itself for learners via the learnerView config
  // (productName/clientName); absent either, this is the generic LV gate.
  const product = productName(cfg)

  async function startSignIn() {
    setState({ kind: 'starting' })
    try {
      const r = await fetch('/LearnerView/api/auth/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      if (r.status === 503) {
        let message = 'Sign-in is not configured for this deployment.'
        try {
          const b = (await r.json()) as { error?: string }
          if (b?.error && b.error !== 'auth not configured') message = b.error
        } catch { /* keep the default */ }
        setState({ kind: 'unconfigured', message })
        return
      }
      if (!r.ok) {
        setState({ kind: 'error', message: `Could not start sign-in (${r.status}).` })
        return
      }
      const body = (await r.json()) as { ssoRedirect?: string }
      if (!body.ssoRedirect) {
        setState({ kind: 'error', message: 'The server returned an unusable sign-in response.' })
        return
      }
      window.location.assign(body.ssoRedirect)
      // No state reset: the browser is leaving for the LMS.
    } catch {
      setState({ kind: 'error', message: 'Could not reach the server. Check your connection and try again.' })
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          background: (t) => `linear-gradient(160deg, ${t.palette.primary.main}14, transparent 55%)`,
        }}
      >
        {/* The public masthead: same destinations as the signed-in bar, all
            LOCKED — every locked click runs the gate's sign-in flow. */}
        <LvHeader
          cfg={cfg}
          homeLabel={homeTabLabel(cfg)}
          homeActive={false}
          homeLocked
          onHome={() => void startSignIn()}
          explore={{
            homeNav: [],
            surfaces: resolveSurfaces(LV_SURFACES, cfg),
            locked: true,
            onHomeNav: () => undefined,
            onSurface: () => undefined,
            onLocked: () => void startSignIn(),
          }}
          account={{
            kind: 'public',
            signingIn: state.kind === 'starting',
            onSignIn: () => void startSignIn(),
          }}
          appearance={{ allowed: appearanceAllowed, mode, onChoose: chooseAppearanceMode }}
        />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
        <Paper elevation={3} sx={{ maxWidth: 400, width: '100%', p: 4, textAlign: 'center' }}>
          {logoUrl && (
            <Box
              component="img" src={logoUrl} alt={brandLogoAlt(cfg)}
              sx={{ height: 36, width: 'auto', maxWidth: 220, objectFit: 'contain', mb: 2 }}
            />
          )}
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {product}
          </Typography>
          {state.kind === 'loading' ? (
            <Box sx={{ py: 3 }}><CircularProgress size={26} /></Box>
          ) : state.kind === 'unconfigured' ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {state.message}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                If you were expecting to sign in, contact your administrator.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Sign in with your learning account to continue.
              </Typography>
              {state.kind === 'error' && (
                <Typography variant="body2" color="error">{state.message}</Typography>
              )}
              <Button
                variant="contained"
                size="large"
                startIcon={state.kind === 'starting' ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
                disabled={state.kind === 'starting'}
                onClick={() => void startSignIn()}
                sx={{ alignSelf: 'center', px: 4 }}
              >
                {state.kind === 'starting' ? 'Redirecting…' : state.kind === 'error' ? 'Try again' : 'Sign in'}
              </Button>
            </Stack>
          )}
        </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
